import { createFileRoute } from "@tanstack/react-router";

import { LegalPage, LegalSection } from "@/components/LegalPage";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — PortDwellr" },
      {
        name: "description",
        content:
          "How PortDwellr collects, uses, and protects yard operations data for drayage yards, cross-docks, and port-adjacent terminals.",
      },
      { property: "og:title", content: "Privacy Policy — PortDwellr" },
      {
        property: "og:description",
        content: "How PortDwellr collects, uses, and protects yard operations data.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" updated="Last updated: August 2026">
      <LegalSection heading="What we collect">
        <p>
          PortDwellr collects the operational data you enter or connect to the platform: yard,
          gate, dock, and staging configuration; carrier and driver records; container and
          appointment records; gate check-in and check-out events; dwell and detention records;
          disputes; and the agent event feed generated by the SENSE → SEQUENCE → ASSIGN → RESOLVE →
          LEARN loop. We also collect account data — name, work email, organization, and role — and
          standard technical logs such as IP address, browser type, and timestamps.
        </p>
      </LegalSection>
      <LegalSection heading="How we use it">
        <p>
          Operational data is used to run the product for your organization: sequencing arrivals,
          assigning dock doors and staging spots, producing timestamp trails for detention
          disputes, and generating throughput and dwell analytics. Account data is used for
          authentication, access control, support, and service notifications such as critical
          detention alerts.
        </p>
      </LegalSection>
      <LegalSection heading="Data isolation">
        <p>
          Every record is scoped to your organization and enforced at the database level with
          row-level security. Members of one organization cannot read or modify another
          organization's yards, containers, disputes, or events.
        </p>
      </LegalSection>
      <LegalSection heading="Sharing">
        <p>
          We do not sell your data. We share it only with infrastructure providers required to
          operate the service — hosting, database, authentication, bot protection, and
          transactional email — and only to the extent needed to deliver the product. We may
          disclose data if legally required.
        </p>
      </LegalSection>
      <LegalSection heading="Retention">
        <p>
          Operational records are retained for as long as your organization maintains an active
          account, because detention disputes depend on a complete historical timestamp trail. On
          account closure we delete or anonymize organization data within 90 days, except where
          retention is legally required.
        </p>
      </LegalSection>
      <LegalSection heading="Your choices">
        <p>
          Organization admins can export, correct, or request deletion of their organization's
          records. Individual users can update or delete their own account details. Requests go to{" "}
          <a className="underline decoration-signal decoration-2 underline-offset-4" href="mailto:upton@portdwellr.com">
            upton@portdwellr.com
          </a>
          .
        </p>
      </LegalSection>
      <LegalSection heading="Contact">
        <p>
          Questions about this policy:{" "}
          <a className="underline decoration-signal decoration-2 underline-offset-4" href="mailto:upton@portdwellr.com">
            upton@portdwellr.com
          </a>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
